import posthog from 'posthog-js';
import {
  BI_EVENT_TEXT_MAX_SIZE,
  BI_MASK_SELECTORS_STRING,
  BI_UNMASK_SELECTORS_STRING,
  BIEvents,
} from './bi-analytics.constants';
import type {
  BIAnalyticsInitOptions,
  BIAnalyticsTenant,
  BIAnalyticsUser,
  BISource,
} from './bi-analytics.types';
import {
  extractActionText,
  getClickAnalyticsElement,
  isBIEventType,
  parseAnalyticsPropsFromAttribute,
} from './bi-analytics.utils';
import { reportTelemetryError } from './report-telemetry-error';
import {
  requiresInitialized,
  requiresTracking,
} from './bi-analytics.decorators';

const ORGANIZATION_GROUP_TYPE = 'organization';

export class BIAnalyticsService {
  private static instance: BIAnalyticsService | undefined;
  private initialized = false;
  private trackingEnabled = false;
  private isDocumentListenerAdded = false;
  private currentPageName: string | undefined;
  private tenantId: string | undefined;
  private tenantName: string | undefined;
  private source: BISource | undefined;

  private constructor() {
    this.trackGlobalClickEvents = this.trackGlobalClickEvents.bind(this);
  }

  private init({ apiKey, apiHost, source }: BIAnalyticsInitOptions) {
    if (this.initialized || !apiKey || !apiHost) {
      return;
    }

    this.source = source;

    posthog.init(apiKey, {
      api_host: apiHost,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_dead_clicks: true,
      session_recording: {
        maskTextSelector: '*',
        maskTextFn: (text, element) => {
          if (!text || !text?.trim()) return text;
          if (element?.closest(BI_MASK_SELECTORS_STRING))
            return '*'.repeat(text.trim().length);
          if (element?.closest(BI_UNMASK_SELECTORS_STRING)) return text;
          return '*'.repeat(text.trim().length);
        },
      },
    });

    this.initialized = true;

    if (!this.isDocumentListenerAdded) {
      document.addEventListener('click', this.trackGlobalClickEvents);
      this.isDocumentListenerAdded = true;
    }
  }

  private trackGlobalClickEvents(event: MouseEvent) {
    const targetElement = getClickAnalyticsElement(event.target);
    if (!targetElement) {
      return;
    }

    this.trackGlobalAutomaticEvents(targetElement);
  }

  private trackGlobalAutomaticEvents(targetElement: Element) {
    try {
      const eventType = targetElement.getAttribute('data-analytics-type');
      if (!eventType || !isBIEventType(eventType)) {
        return;
      }

      const analyticsProps = parseAnalyticsPropsFromAttribute(
        targetElement.getAttribute('data-analytics-props'),
      );
      const { disabled, ...eventProperties } = analyticsProps ?? {};

      if (disabled) {
        return;
      }

      const actionText = extractActionText({
        element: targetElement,
        maxSize: BI_EVENT_TEXT_MAX_SIZE,
      });

      this.trackEvent(eventType, {
        ...eventProperties,
        text: actionText,
      });
    } catch (error: unknown) {
      reportTelemetryError(error, {
        context: 'BIAnalyticsService.trackGlobalAutomaticEvents',
      });
    }
  }

  static getInstance(initOptions: BIAnalyticsInitOptions): BIAnalyticsService {
    if (!BIAnalyticsService.instance) {
      BIAnalyticsService.instance = new BIAnalyticsService();
    }

    BIAnalyticsService.instance.init(initOptions);
    return BIAnalyticsService.instance;
  }

  static tryGetInstance(): BIAnalyticsService | undefined {
    return BIAnalyticsService.instance;
  }

  @requiresTracking
  trackLogout(properties?: Record<string, unknown>) {
    this.trackEvent(BIEvents.LOGOUT, properties);
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  isTrackingEnabled(): boolean {
    return this.trackingEnabled;
  }

  @requiresInitialized
  setTrackingEnabled(enabled: boolean) {
    if (this.trackingEnabled === enabled) {
      return;
    }

    this.trackingEnabled = enabled;

    if (!enabled) {
      this.currentPageName = undefined;
      posthog.reset();
      return;
    }

    this.applyTenantGroup();
  }

  @requiresTracking
  setUser({ userId, email, name }: BIAnalyticsUser) {
    if (!userId) {
      return;
    }

    posthog.identify(userId, {
      ...(email ? { email } : {}),
      ...(name ? { name } : {}),
      source: this.source,
    });
  }

  setTenant({ tenantId, tenantName }: BIAnalyticsTenant) {
    if (!tenantId) {
      return;
    }

    const tenantChanged = Boolean(this.tenantId && this.tenantId !== tenantId);

    this.tenantId = tenantId;
    if (tenantName) {
      this.tenantName = tenantName;
    } else if (tenantChanged) {
      this.tenantName = undefined;
    }

    if (this.isInitialized() && this.isTrackingEnabled()) {
      if (tenantChanged) {
        posthog.resetGroups();
        posthog.sessionManager?.resetSessionId();
      }
      this.applyTenantGroup();
    }
  }

  private applyTenantGroup() {
    if (!this.tenantId || !this.tenantName) {
      return;
    }

    posthog.group(ORGANIZATION_GROUP_TYPE, this.tenantId, {
      name: this.tenantName,
    });
  }

  @requiresTracking
  trackPageView(name: string, properties?: Record<string, unknown>) {
    this.currentPageName = name;
    this.trackEvent(BIEvents.PAGE_VIEW, properties);
  }

  @requiresTracking
  trackEvent(name: string, properties?: Record<string, unknown>) {
    if (!name) {
      return;
    }

    posthog.capture(name, {
      ...properties,
      pageName: this.currentPageName,
      tenantId: this.tenantId,
      source: this.source,
    });
  }

  async unregister() {
    if (this.isDocumentListenerAdded) {
      document.removeEventListener('click', this.trackGlobalClickEvents);
      this.isDocumentListenerAdded = false;
    }

    if (this.initialized) {
      posthog.reset();

      try {
        await posthog.shutdown();
      } catch (error: unknown) {
        reportTelemetryError(error, {
          context: 'BIAnalyticsService.unregister',
        });
      }
    }

    this.trackingEnabled = false;
    this.initialized = false;
    this.source = undefined;
    this.currentPageName = undefined;
    this.tenantId = undefined;
    this.tenantName = undefined;

    if (BIAnalyticsService.instance === this) {
      BIAnalyticsService.instance = undefined;
    }
  }
}
